import React, { useEffect, useState } from 'react';
import { Trophy, Loader2, Medal, Clock, Repeat, Award, Flame, TrendingDown, Gauge, Wrench, AlertTriangle, Activity, Package } from 'lucide-react';
import { PageHeader } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { awardIcon, awardColor } from '../utils/awardStyles';
import api from '../api/client';

const fmtMins = (m) => { m = Math.round(m || 0); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };
const MEDALS = ['#f0a92b', '#c0c4cc', '#cd7f32']; // gold, silver, bronze
const PERIODS = [{ k: 'week', tkey: 'lb.week' }, { k: 'month', tkey: 'lb.month' }, { k: 'all', tkey: 'lb.all' }];
const FACTOR_META = {
  changeover: { tkey: 'lb.factor.changeover', icon: Wrench, color: '#a855f7' },
  problems: { tkey: 'lb.factor.problems', icon: AlertTriangle, color: '#ef4444' },
  uptime: { tkey: 'lb.factor.uptime', icon: Activity, color: '#3fb978' },
  production: { tkey: 'lb.factor.production', icon: Package, color: '#38bdf8' },
};

function FactorChips({ factors }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap gap-1.5 justify-center">
      {Object.entries(FACTOR_META).map(([k, meta]) => {
        if (!(k in factors)) return null;
        const Icon = meta.icon;
        return (
          <span key={k} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5" data-testid={`factor-chip-${k}`}
            style={{ fontSize: 10, background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)', color: 'var(--kmt-muted)' }}
            title={`${t(meta.tkey)}: ${factors[k]}/100`}>
            <Icon size={10} style={{ color: meta.color }} />{factors[k]}
          </span>
        );
      })}
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const me = user?.name || '';
  const [view, setView] = useState('overall'); // 'overall' | 'changeover'
  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState('');
  const [period, setPeriod] = useState('month');
  const [reason, setReason] = useState('');
  const [reasons, setReasons] = useState([]);
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [badgeData, setBadgeData] = useState({ badges: [], streaks: [], mostImproved: null });
  const [awardsWall, setAwardsWall] = useState(null);
  const [loading, setLoading] = useState(true);

  const month = new Date().toISOString().slice(0, 7);
  const periodLabel = t(PERIODS.find((p) => p.k === period)?.tkey || 'lb.month');

  const loadChangeover = (mid = machine, p = period, r = reason) => {
    api.settingReport(month, mid || undefined, p, r || undefined).then(setData).catch(() => setData(null));
  };
  const loadPerf = (p = period) => {
    api.operatorPerformance(p).then(setPerf).catch(() => setPerf(null));
  };
  const loadBadges = () => api.operatorBadges().then(setBadgeData).catch(() => {});
  const loadAwardsWall = () => api.awardsLeaderboard().then(setAwardsWall).catch(() => {});

  const refresh = (v = view, p = period) => {
    setLoading(true);
    const task = v === 'overall' ? loadPerf(p) : loadChangeover(machine, p, reason);
    Promise.resolve(task).finally(() => setLoading(false));
  };

  useEffect(() => { api.listMachines().then(setMachines).catch(() => {}); api.listSettingReasons().then(setReasons).catch(() => {}); loadBadges(); loadAwardsWall(); }, []);
  useEffect(() => { refresh(view, period); /* eslint-disable-next-line */ }, [view, period, machine, reason]);
  useRealtime(() => { if (view === 'overall') loadPerf(period); else loadChangeover(machine, period, reason); loadBadges(); loadAwardsWall(); });

  const board = data?.byOperator || [];
  const perfBoard = perf?.operators || [];
  const badges = badgeData.badges || [];
  const streaks = badgeData.streaks || [];
  const mostImproved = badgeData.mostImproved;
  const isOverall = view === 'overall';

  // Medals earned per operator = number of "Fastest this month" wins
  const medalsByOp = {};
  badges.forEach((b) => { medalsByOp[b.operator] = (medalsByOp[b.operator] || 0) + 1; });
  const myMedals = medalsByOp[me] || 0;
  const myStreak = streaks.find((s) => s.operator === me && s.current);
  const myRank = isOverall
    ? perfBoard.findIndex((o) => o.operator === me)
    : board.findIndex((o) => o.operator === me);
  const myEntry = myRank >= 0 ? (isOverall ? perfBoard[myRank] : board[myRank]) : null;

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Trophy} title={t('lb.title')}
        subtitle={isOverall ? `${t('lb.overallSub')} · ${periodLabel}` : `${t('lb.changeoverSub')} · ${periodLabel}`}
        right={!isOverall && (
          <div className="flex flex-col sm:flex-row gap-2">
            <select className="kmt-input md:w-44" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leaderboard-reason-filter">
              <option value="">{t('lb.allReasons')}</option>
              {reasons.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
              <option value="Unspecified">{t('lb.unspecified')}</option>
            </select>
            <select className="kmt-input md:w-52" value={machine} onChange={(e) => setMachine(e.target.value)} data-testid="leaderboard-machine-filter">
              <option value="">{t('lb.allMachines')}</option>
              {machines.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.displayName}</option>)}
            </select>
          </div>
        )} />

      <div className="p-8 space-y-6" data-testid="leaderboard-page">
        {/* View toggle */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg p-1 gap-1" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
            {[{ k: 'overall', label: t('lb.overall'), icon: Gauge }, { k: 'changeover', label: t('lb.changeover'), icon: Wrench }].map((v) => {
              const Icon = v.icon;
              return (
                <button key={v.k} onClick={() => setView(v.k)} data-testid={`leaderboard-view-${v.k}`}
                  className="px-4 py-1.5 rounded-md text-sm transition-colors inline-flex items-center gap-1.5"
                  style={view === v.k ? { background: 'var(--kmt-gold)', color: '#17140a', fontWeight: 600 } : { color: 'var(--kmt-muted)', background: 'transparent' }}>
                  <Icon size={14} />{v.label}
                </button>
              );
            })}
          </div>
          <div className="inline-flex rounded-lg p-1 gap-1" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
            {PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`leaderboard-period-${p.k}`}
                className="px-4 py-1.5 rounded-md text-sm transition-colors"
                style={period === p.k ? { background: 'var(--kmt-gold)', color: '#17140a', fontWeight: 600 } : { color: 'var(--kmt-muted)', background: 'transparent' }}>
                {t(p.tkey)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <>
            {/* My standing — shown when the signed-in person is on the board or has medals */}
            {(myEntry || myMedals > 0 || myStreak) && (
              <div className="kmt-panel p-5 flex flex-wrap items-center gap-5" data-testid="my-standing"
                style={{ borderColor: 'var(--kmt-gold)', background: 'linear-gradient(90deg, rgba(240,169,43,0.08), transparent)' }}>
                <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 54, height: 54, background: 'rgba(240,169,43,0.14)' }}>
                  <Gauge size={26} style={{ color: 'var(--kmt-gold)' }} />
                </div>
                <div className="min-w-0">
                  <div className="kmt-label">{t('lb.yourStanding')} · {me}</div>
                  <div className="font-display font-semibold text-lg">
                    {myEntry ? `${t('lb.rank')} #${myRank + 1}` : t('lb.notRanked')}
                    {myEntry && isOverall && <span className="ml-2" style={{ color: 'var(--kmt-gold)' }}>· {myEntry.score}/100</span>}
                    {myEntry && !isOverall && <span className="ml-2" style={{ color: 'var(--kmt-gold)' }}>· {t('lb.colAvg').toLowerCase()} {fmtMins(myEntry.avgMinutes)}</span>}
                  </div>
                  {!myEntry && <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('lb.climbHint')}</div>}
                </div>
                {/* My earned medals */}
                <div className="flex items-center gap-4 ml-auto" data-testid="my-medals">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 font-display font-bold" style={{ fontSize: 22, color: 'var(--kmt-gold)' }}>
                      <Trophy size={18} />{myMedals}
                    </div>
                    <div className="kmt-label">{t('lb.medals')}</div>
                  </div>
                  {myStreak && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 font-display font-bold" style={{ fontSize: 22, color: '#f0783a' }}>
                        <Flame size={18} />{myStreak.length}
                      </div>
                      <div className="kmt-label">{t('lb.streak')}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isOverall
              ? <OverallView board={perfBoard} periodLabel={periodLabel} me={me} medalsByOp={medalsByOp} />
              : <ChangeoverView board={board} periodLabel={periodLabel} streaks={streaks} mostImproved={mostImproved} badges={badges} me={me} medalsByOp={medalsByOp} />}

            <AwardsWall data={awardsWall} me={me} />
          </>
        )}
      </div>
    </div>
  );
}

function AwardsWall({ data, me }) {
  const { t } = useI18n();
  const rows = data?.operators || [];
  if (!rows.length) return null;
  const max = rows[0].count || 1;
  return (
    <div className="kmt-panel p-6" data-testid="awards-wall">
      <div className="flex items-center gap-2 mb-4">
        <Award size={17} style={{ color: 'var(--kmt-gold)' }} />
        <div className="font-display font-semibold">{t('lb.awardsWall')}</div>
        <span className="kmt-label ml-auto">{t('lb.awardsWallSub').replace('{n}', data.total)}</span>
      </div>
      <div className="space-y-2.5">
        {rows.map((r, i) => {
          const mine = r.operator === me;
          return (
            <div key={r.operator} className="flex items-center gap-3" data-testid={`awards-wall-row-${i}`}
              style={mine ? { background: 'rgba(240,169,43,0.08)', borderRadius: 10, padding: '4px 8px', margin: '0 -8px' } : undefined}>
              <span className="font-mono-k text-sm w-6 text-center shrink-0" style={{ color: i < 3 ? MEDALS[i] : 'var(--kmt-muted)' }}>{i + 1}</span>
              <span className="text-sm font-medium w-40 truncate shrink-0">{r.operator}{mine && <span style={{ color: 'var(--kmt-gold)' }}> · {t('lb.you')}</span>}</span>
              <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--kmt-panel-2)' }}>
                <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: 'var(--kmt-gold)' }} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {(r.recent || []).map((a) => {
                  const Ico = awardIcon(a.icon);
                  return <span key={a.id} title={a.title} className="flex items-center justify-center rounded-full" style={{ width: 24, height: 24, background: `${awardColor(a.color)}22` }}><Ico size={13} style={{ color: awardColor(a.color) }} /></span>;
                })}
              </div>
              <span className="font-display font-bold w-10 text-right shrink-0" style={{ color: 'var(--kmt-gold)' }}>{r.count}</span>
              {r.monthCount > 0 && <span className="kmt-badge s-completed shrink-0" style={{ fontSize: 9 }}>+{r.monthCount} {t('lb.thisMonth')}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MedalCell({ count }) {
  if (!count) return <span style={{ color: 'var(--kmt-muted-2)' }}>—</span>;
  return (
    <span className="inline-flex items-center gap-1" style={{ color: 'var(--kmt-gold)' }} title={`${count} "Fastest this month" medal${count > 1 ? 's' : ''}`}>
      <Trophy size={13} />{count}
    </span>
  );
}

function OverallView({ board, periodLabel, me, medalsByOp }) {
  const { t } = useI18n();
  if (board.length === 0) {
    return (
      <div className="kmt-panel p-10 text-center" style={{ color: 'var(--kmt-muted)' }} data-testid="overall-empty">
        <Gauge size={32} className="mx-auto mb-3" style={{ color: 'var(--kmt-muted-2)' }} />
        {t('lb.overallEmpty')}
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="overall-view">
      {/* Podium top 3 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {board.slice(0, 3).map((o, i) => (
          <div key={o.operator} className="kmt-panel p-5 flex flex-col items-center text-center" data-testid={`overall-podium-${i}`}
            style={{ borderColor: i === 0 ? 'var(--kmt-gold)' : 'var(--kmt-border)', order: i === 0 ? 2 : (i === 1 ? 1 : 3) }}>
            <Medal size={30} style={{ color: MEDALS[i] }} />
            <div className="font-display font-semibold mt-2 text-lg">{o.operator}{o.operator === me && <span className="kmt-badge s-running ml-2" style={{ fontSize: 9 }}>{t('lb.you')}</span>}</div>
            <div className="kmt-label mt-1">#{i + 1}</div>
            <div className="font-display font-bold mt-3" style={{ fontSize: 34, color: i === 0 ? 'var(--kmt-gold)' : 'var(--kmt-text)' }}>{o.score}</div>
            <div className="text-xs mb-3" style={{ color: 'var(--kmt-muted)' }}>{t('lb.scoreOf100')}</div>
            <FactorChips factors={o.factors} />
          </div>
        ))}
      </div>

      {/* Full ranking */}
      <div className="kmt-panel p-0 overflow-hidden">
        <table className="w-full">
          <thead><tr>
            <th className="kmt-th" style={{ width: 60 }}>{t('lb.rank')}</th>
            <th className="kmt-th">{t('col.operator')}</th>
            <th className="kmt-th"><span className="inline-flex items-center gap-1"><Gauge size={12} />{t('lb.score')}</span></th>
            <th className="kmt-th"><span className="inline-flex items-center gap-1"><Wrench size={12} />{t('lb.colChangeover')}</span></th>
            <th className="kmt-th"><span className="inline-flex items-center gap-1"><AlertTriangle size={12} />{t('lb.colProblems')}</span></th>
            <th className="kmt-th"><span className="inline-flex items-center gap-1"><Activity size={12} />{t('lb.colUptime')}</span></th>
            <th className="kmt-th"><span className="inline-flex items-center gap-1"><Package size={12} />{t('lb.colProduced')}</span></th>
            <th className="kmt-th"><span className="inline-flex items-center gap-1"><Trophy size={12} />{t('lb.medals')}</span></th>
          </tr></thead>
          <tbody>
            {board.map((o, i) => {
              const mine = o.operator === me;
              return (
              <tr key={o.operator} data-testid={`overall-row-${i}`} style={mine ? { background: 'rgba(240,169,43,0.08)' } : undefined}>
                <td className="kmt-td" data-label="Rank">
                  <span className="flex items-center justify-center rounded-full font-bold" style={{ width: 24, height: 24, fontSize: 11, background: i < 3 ? MEDALS[i] : 'var(--kmt-panel-2)', color: i < 3 ? '#17140a' : 'var(--kmt-muted)' }}>{i + 1}</span>
                </td>
                <td className="kmt-td" data-label="Operator" style={{ fontWeight: 500 }}>{o.operator}{mine && <span className="kmt-badge s-running ml-2" style={{ fontSize: 9 }}>{t('lb.you')}</span>}</td>
                <td className="kmt-td font-mono-k" data-label="Score" style={{ color: i === 0 ? 'var(--kmt-gold)' : 'inherit', fontWeight: 700 }}>{o.score}</td>
                <td className="kmt-td font-mono-k" data-label="Changeover" style={{ color: 'var(--kmt-muted)' }}>{o.changeoverSessions ? `${o.factors.changeover} · ${t('lb.colAvg').toLowerCase()} ${fmtMins(o.changeoverAvg)}` : '—'}</td>
                <td className="kmt-td font-mono-k" data-label="Problems" style={{ color: 'var(--kmt-muted)' }}>{o.factors.problems} · {o.problemCount} · {fmtMins(o.problemDowntime)}</td>
                <td className="kmt-td font-mono-k" data-label="Uptime" style={{ color: 'var(--kmt-muted)' }}>{o.factors.uptime} · {fmtMins(o.uptimeMinutes)}</td>
                <td className="kmt-td font-mono-k" data-label="Produced" style={{ color: 'var(--kmt-muted)' }}>{o.factors.production} · {o.producedUnits}</td>
                <td className="kmt-td" data-label="Medals"><MedalCell count={medalsByOp[o.operator]} /></td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>
        {t('lb.scoreNote')}
      </div>
    </div>
  );
}

function ChangeoverView({ board, periodLabel, streaks, mostImproved, badges, me, medalsByOp }) {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      {(mostImproved || streaks.some((s) => s.current)) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {streaks.filter((s) => s.current).slice(0, 1).map((s) => (
            <div key="streak" className="kmt-panel p-4 flex items-center gap-3" data-testid="leaderboard-streak" style={{ borderColor: 'rgba(240,120,43,0.5)' }}>
              <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 42, height: 42, background: 'rgba(240,120,43,0.14)' }}>
                <Flame size={22} style={{ color: '#f0783a' }} />
              </div>
              <div>
                <div className="font-display font-semibold">{s.operator} · {t('awards.streakTitle').replace('{n}', s.length)}</div>
                <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('awards.streakDesc').replace('{months}', s.months.join(' → '))}</div>
              </div>
            </div>
          ))}
          {mostImproved && (
            <div className="kmt-panel p-4 flex items-center gap-3" data-testid="leaderboard-most-improved" style={{ borderColor: 'rgba(63,185,120,0.5)' }}>
              <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 42, height: 42, background: 'rgba(63,185,120,0.14)' }}>
                <TrendingDown size={22} style={{ color: 'var(--kmt-green)' }} />
              </div>
              <div>
                <div className="font-display font-semibold">{t('lb.mostImproved')} · {mostImproved.operator}</div>
                <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('awards.mostImprovedDesc').replace('{imp}', fmtMins(mostImproved.improvement)).replace('{prev}', fmtMins(mostImproved.previousAvg)).replace('{cur}', fmtMins(mostImproved.currentAvg))}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {board.length === 0 ? (
        <div className="kmt-panel p-10 text-center" style={{ color: 'var(--kmt-muted)' }}>
          <Trophy size={32} className="mx-auto mb-3" style={{ color: 'var(--kmt-muted-2)' }} />
          {t('lb.changeoverEmpty')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {board.slice(0, 3).map((o, i) => (
              <div key={o.operator} className="kmt-panel p-5 flex flex-col items-center text-center" data-testid={`podium-${i}`}
                style={{ borderColor: i === 0 ? 'var(--kmt-gold)' : 'var(--kmt-border)', order: i === 0 ? 2 : (i === 1 ? 1 : 3) }}>
                <Medal size={30} style={{ color: MEDALS[i] }} />
                <div className="font-display font-semibold mt-2 text-lg">{o.operator}{o.operator === me && <span className="kmt-badge s-running ml-2" style={{ fontSize: 9 }}>{t('lb.you')}</span>}</div>
                <div className="kmt-label mt-1">#{i + 1}</div>
                <div className="font-display font-bold mt-3" style={{ fontSize: 30, color: i === 0 ? 'var(--kmt-gold)' : 'var(--kmt-text)' }}>{fmtMins(o.avgMinutes)}</div>
                <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('lb.avgPer')}</div>
                <div className="text-xs mt-2" style={{ color: 'var(--kmt-muted)' }}>{o.sessions} {t('lb.changeovers')} · {fmtMins(o.totalMinutes)} {t('lb.total')}</div>
              </div>
            ))}
          </div>

          <div className="kmt-panel p-0 overflow-hidden">
            <table className="w-full">
              <thead><tr>
                <th className="kmt-th" style={{ width: 60 }}>{t('lb.rank')}</th>
                <th className="kmt-th">{t('col.operator')}</th>
                <th className="kmt-th"><span className="inline-flex items-center gap-1"><Clock size={12} />{t('lb.colAvg')}</span></th>
                <th className="kmt-th"><span className="inline-flex items-center gap-1"><Repeat size={12} />{t('lb.colChangeovers')}</span></th>
                <th className="kmt-th">{t('lb.colTotal')}</th>
                <th className="kmt-th"><span className="inline-flex items-center gap-1"><Trophy size={12} />{t('lb.medals')}</span></th>
              </tr></thead>
              <tbody>
                {board.map((o, i) => {
                  const mine = o.operator === me;
                  return (
                  <tr key={o.operator} data-testid={`leaderboard-row-${i}`} style={mine ? { background: 'rgba(240,169,43,0.08)' } : undefined}>
                    <td className="kmt-td" data-label="Rank">
                      <span className="flex items-center justify-center rounded-full font-bold" style={{ width: 24, height: 24, fontSize: 11, background: i < 3 ? MEDALS[i] : 'var(--kmt-panel-2)', color: i < 3 ? '#17140a' : 'var(--kmt-muted)' }}>{i + 1}</span>
                    </td>
                    <td className="kmt-td" data-label="Operator" style={{ fontWeight: 500 }}>{o.operator}{mine && <span className="kmt-badge s-running ml-2" style={{ fontSize: 9 }}>{t('lb.you')}</span>}</td>
                    <td className="kmt-td font-mono-k" data-label="Avg" style={{ color: i === 0 ? 'var(--kmt-gold)' : 'inherit' }}>{fmtMins(o.avgMinutes)}</td>
                    <td className="kmt-td font-mono-k" data-label="Changeovers" style={{ color: 'var(--kmt-muted)' }}>{o.sessions}</td>
                    <td className="kmt-td font-mono-k" data-label="Total time" style={{ color: 'var(--kmt-muted)' }}>{fmtMins(o.totalMinutes)}</td>
                    <td className="kmt-td" data-label="Medals"><MedalCell count={medalsByOp[o.operator]} /></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('lb.changeoverNote')}</div>
        </>
      )}

      {badges.length > 0 && (
        <div className="kmt-panel p-6" data-testid="leaderboard-hall-of-fame">
          <div className="flex items-center gap-2 mb-4">
            <Award size={17} style={{ color: 'var(--kmt-gold)' }} />
            <div className="font-display font-semibold">{t('lb.hallOfFame')}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {badges.map((b) => (
              <div key={b.month} className="flex items-center gap-3 rounded-lg px-4 py-3" data-testid={`badge-${b.month}`}
                style={{ background: 'var(--kmt-panel-2)', border: `1px solid ${b.current ? 'var(--kmt-gold)' : 'var(--kmt-border)'}` }}>
                <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 40, height: 40, background: 'rgba(240,169,43,0.12)' }}>
                  <Trophy size={20} style={{ color: 'var(--kmt-gold)' }} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-medium truncate">{b.operator}</div>
                  <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{b.monthLabel} · {t('lb.colAvg').toLowerCase()} {fmtMins(b.avgMinutes)}{b.current ? ` · ${t('awards.current')}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
